/**
 * How a model's conjectures have fared when somebody tested them — its insights,
 * in the reader's word (D333), which is the word every line below prints.
 *
 * Per model, never pooled, for the reason the calibration ledger gives: a
 * conjecture is its proposing model's, and a figure mixing two models would
 * describe neither. Counted, never scored — how many of its conjectures carry a
 * recorded result, and how many of those went the way the prediction said —
 * because the selection is the person's: people test what they doubt and what is
 * cheap to test, so this is a record of the ones somebody chose to test, never a
 * rate at which a model's conjectures hold. Every line that prints it says so.
 *
 * Below `MIN_SCORED` results no line is printed at all, only the count and how
 * many more are wanted — calibration's own threshold, so the two ledgers become
 * readable at the same point.
 *
 * What counts is decided by the record alone (D282):
 *
 * - **A result is scored only against the wording it tested.** A conjecture whose
 *   current registration postdates its result was rewritten after the result
 *   came in, and a prediction written after its answer is not a prediction. It is
 *   counted as rewritten after its result, and never scored.
 * - **A conjecture you rewrote is yours.** Any rewrite by hand leaves the claim
 *   yours rather than the model's, as the calibration ledger reads it; it is
 *   counted apart and never scored against the model.
 * - **"Did not settle it" is counted and never scored**, as calibration counts
 *   an inconclusive result: a test that settled nothing is evidence of neither.
 *
 * Pure: no store, no clock, no model.
 */

import { MIN_SCORED, modelKey } from './calibration'
import type { ModelStamp, Outcome, ReasonNode, Session } from './types'

/** One conjecture a model proposed and somebody recorded a result against. */
export interface ConjectureResult {
  sessionId: string
  nodeId: string
  label: string
  /** The model that proposed it. */
  model: ModelStamp
  bearing: Outcome['bearing']
  recordedAt: number
  /** When the wording it carries now was registered. */
  registeredAt: number
  /** Rewritten by hand at any point: the claim is the person's now. */
  rewritten: boolean
}

/** The conjectures in one exploration that carry a recorded result, with a
 *  proposing model's stamp. */
export function conjectureResultsOf(session: Pick<Session, 'id' | 'order' | 'nodes'>) {
  const out: ConjectureResult[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    const row = conjectureResultOf(session.id, n)
    if (row) out.push(row)
  }
  return out
}

/** One node as a result row, or null when it is not a tested conjecture a model
 *  proposed. Shared with the library walk in `persist.ts`. */
export function conjectureResultOf(
  sessionId: string,
  n: ReasonNode | undefined,
): ConjectureResult | null {
  if (!n || n.kind !== 'conjecture' || !n.conjecture || !n.outcome || !n.model) return null
  return {
    sessionId,
    nodeId: n.id,
    label: n.label,
    model: n.model,
    bearing: n.outcome.bearing,
    recordedAt: n.outcome.recordedAt,
    registeredAt: n.conjecture.registered.at,
    rewritten: n.origin === 'manual' || n.editedAt !== undefined,
  }
}

/** One model's record. Counts only; no field combines them. */
export interface TrackRecordRow {
  key: string
  model: ModelStamp
  /** Every tested conjecture of this model's, scored or not. */
  results: number
  /** Results scored against the wording they tested. */
  scored: number
  /** Scored results that went the way the prediction said. */
  predicted: number
  /** Scored results that went against it. */
  against: number
  /** Scored results that did not settle it. */
  unsettled: number
  /** Results under a registration written after them. */
  rewrittenAfter: number
  /** Results on a conjecture you rewrote by hand. */
  yours: number
  explorations: number
}

/**
 * The record, per model. A result is scored when the model's own wording was
 * registered before the result was recorded; the rest are counted apart.
 */
export function trackRecord(rows: readonly ConjectureResult[]): TrackRecordRow[] {
  const byModel = new Map<string, { row: TrackRecordRow; sessions: Set<string> }>()
  for (const r of rows) {
    const key = modelKey(r.model)
    let held = byModel.get(key)
    if (!held) {
      held = {
        row: {
          key,
          model: r.model,
          results: 0,
          scored: 0,
          predicted: 0,
          against: 0,
          unsettled: 0,
          rewrittenAfter: 0,
          yours: 0,
          explorations: 0,
        },
        sessions: new Set(),
      }
      byModel.set(key, held)
    }
    const row = held.row
    row.results++
    held.sessions.add(r.sessionId)
    if (r.rewritten) {
      row.yours++
      continue
    }
    if (r.registeredAt > r.recordedAt) {
      row.rewrittenAfter++
      continue
    }
    row.scored++
    if (r.bearing === 'supported') row.predicted++
    else if (r.bearing === 'refuted') row.against++
    else row.unsettled++
  }
  return [...byModel.values()]
    .map(({ row, sessions }) => ({ ...row, explorations: sessions.size }))
    .sort((a, b) => b.results - a.results || a.key.localeCompare(b.key))
}

/**
 * One model's record as the one sentence every surface prints — or, below
 * `MIN_SCORED` scored results, how many more are wanted before there is one.
 */
export function trackRecordLine(r: TrackRecordRow): string {
  const apart = [
    r.rewrittenAfter
      ? `${r.rewrittenAfter} rewritten after ${r.rewrittenAfter === 1 ? 'its' : 'their'} result, not scored`
      : '',
    r.yours ? `${r.yours} rewritten by you, not counted as ${r.model.name}'s` : '',
  ].filter(Boolean)
  const tail = apart.length ? ` (${apart.join('; ')})` : ''
  if (r.scored < MIN_SCORED) {
    const more = MIN_SCORED - r.scored
    return `${r.scored} of ${r.model.name}'s insights ${r.scored === 1 ? 'has' : 'have'} a result scored against the wording it tested — ${more} more before a track record is printed.${tail}`
  }
  return `Among the ones you chose to test: ${r.predicted} of ${r.scored} of ${r.model.name}'s insights went the way the prediction said, ${r.against} went against it${r.unsettled ? `, ${r.unsettled} did not settle it` : ''}, across ${r.explorations} exploration${r.explorations === 1 ? '' : 's'}. A record of what you tested, not a rate at which its insights hold.${tail}`
}
